import { useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { CommunitySearchResults } from '~/interfaces/community';

import { communitySearchKeys, fetchCommunitySearch, SEARCH_DEBOUNCE_MS } from '../_utils/communitySearch';
import type { CommunityPostsPage } from './useCommunityPosts';
import { useDebouncedCallback } from './useDebouncedCallback';

// --- Trending posts (cached, passive) ---
// "Trending" is the top of the posts feed, not a search — fetch the posts proxy directly.

interface UseCommunityTrendingPostsOptions {
  enabled?: boolean;
}

const fetchTrendingPosts = async (): Promise<CommunityPostsPage> => {
  const response = await fetch('/community/posts?limit=5');
  if (!response.ok) throw new Error('Failed to fetch trending posts');
  return response.json();
};

const useCommunityTrendingPosts = ({ enabled = true }: UseCommunityTrendingPostsOptions = {}) => {
  return useQuery({
    queryKey: communitySearchKeys.trending,
    queryFn: fetchTrendingPosts,
    enabled,
  });
};

// --- Search (debounced query, cached per query string) ---

interface UseCommunitySearchOptions {
  initialQuery?: string;
  /** Pre-fetched data (e.g. from SSR loader) to seed the cache and avoid a duplicate client-side fetch */
  initialData?: CommunitySearchResults;
}

interface UseCommunitySearchReturn {
  results?: CommunitySearchResults;
  isSearching: boolean;
  search: (query: string) => void;
}

const useCommunitySearch = ({
  initialQuery,
  initialData,
}: UseCommunitySearchOptions = {}): UseCommunitySearchReturn => {
  // Capture the initial query once so it doesn't drift when searchParams update the prop
  const initialQueryRef = useRef(initialQuery?.trim() ?? '');
  const [debouncedQuery, setDebouncedQuery] = useState(initialQueryRef.current);
  const [isPending, setIsPending] = useState(false);

  const debouncedSetQuery = useDebouncedCallback((q: string) => {
    setDebouncedQuery(q);
    setIsPending(false);
  }, SEARCH_DEBOUNCE_MS);

  const { data, isFetching } = useQuery({
    queryKey: communitySearchKeys.query(debouncedQuery),
    queryFn: () => fetchCommunitySearch({ q: debouncedQuery }),
    enabled: !!debouncedQuery,
    // Seed cache with SSR data so the initial query doesn't trigger a redundant client-side fetch.
    // Only apply when the current query matches the initial one — otherwise useQuery skips fetching.
    initialData: debouncedQuery === initialQueryRef.current ? initialData : undefined,
  });

  const search = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      debouncedSetQuery.cancel();
      setDebouncedQuery('');
      setIsPending(false);
      return;
    }
    setIsPending(true);
    debouncedSetQuery(trimmed);
  };

  return {
    results: data,
    isSearching: (isPending || (!!debouncedQuery && isFetching)) && !data,
    search,
  };
};

export type { UseCommunitySearchOptions, UseCommunitySearchReturn, UseCommunityTrendingPostsOptions };
export { useCommunitySearch, useCommunityTrendingPosts };
